/**
 * Video Popup Web Component
 * Displays a mini video player in the corner that expands to a modal when clicked.
 * Supports lazy loading, session storage for dismissed state, and autoplay with muted audio.
 */

class VideoPopup extends HTMLElement {
  constructor() {
    super();
    this.hasLazyLoaded = false;
    this.modalVideoLoaded = false;
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.storageKey = 'video-popup-dismissed-' + this.sectionId;

    this.miniPlayer = this.querySelector('.video-popup-mini');
    this.modal = this.querySelector('.video-popup-modal');
    this.miniVideo = this.querySelector('.video-popup-mini__video');
    this.modalVideo = this.querySelector('.video-popup-modal__video');

    if (!this.miniPlayer || !this.modal) return;

    // Check if user has dismissed the video popup in this session
    if (sessionStorage.getItem(this.storageKey) === 'true') {
      this.miniPlayer.classList.add('is-hidden');
      return;
    }

    this.setupEventListeners();
    this.loadAndShowVideo();
  }

  loadAndShowVideo() {
    const source = this.miniVideo.querySelector('source');
    if (!source || !source.dataset.src) return;

    // Set up the video source
    source.src = source.dataset.src;
    this.miniVideo.load();

    // Wait for video to have enough data before showing
    this.miniVideo.addEventListener('canplay', () => {
      this.classList.add('is-ready');
      this.miniVideo.play().catch(() => {});
      this.hasLazyLoaded = true;
    }, { once: true });

    // Fallback: if video takes too long, show anyway after 3 seconds
    setTimeout(() => {
      if (!this.classList.contains('is-ready')) {
        this.classList.add('is-ready');
        this.miniVideo.play().catch(() => {});
        this.hasLazyLoaded = true;
      }
    }, 3000);
  }

  loadModalVideo() {
    if (this.modalVideoLoaded) return;

    const source = this.modalVideo.querySelector('source');
    if (source && source.dataset.src) {
      source.src = source.dataset.src;
      this.modalVideo.load();
      this.modalVideoLoaded = true;
    }
  }

  setupEventListeners() {
    // Mini player click events
    this.miniPlayer.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]');
      if (!action) {
        this.openModal();
        return;
      }

      const actionType = action.dataset.action;
      if (actionType === 'close-mini') {
        e.stopPropagation();
        this.closeMini();
      } else if (actionType === 'expand') {
        e.stopPropagation();
        this.openModal();
      }
    });

    // Modal click events
    this.modal.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]');
      if (!action) return;

      const actionType = action.dataset.action;
      if (actionType === 'close-modal') {
        this.closeModal();
      } else if (actionType === 'toggle-play') {
        this.togglePlay();
      } else if (actionType === 'toggle-mute') {
        this.toggleMute();
      }
    });

    // Close modal on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.hasAttribute('open')) {
        this.closeModal();
      }
    });
  }

  openModal() {
    this.loadModalVideo();
    this.miniPlayer.classList.add('is-hidden');
    this.modal.setAttribute('open', '');
    this.modal.setAttribute('aria-hidden', 'false');
    this.miniVideo.pause();
    this.modalVideo.currentTime = this.miniVideo.currentTime;
    this.modalVideo.play().catch(() => {});
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.modal.removeAttribute('open');
    this.modal.setAttribute('aria-hidden', 'true');
    this.miniPlayer.classList.remove('is-hidden');
    this.modalVideo.pause();
    this.miniVideo.currentTime = this.modalVideo.currentTime;
    this.miniVideo.play().catch(() => {});
    document.body.style.overflow = '';
  }

  closeMini() {
    this.miniPlayer.classList.add('is-hidden');
    this.miniVideo.pause();
    sessionStorage.setItem(this.storageKey, 'true');
  }

  togglePlay() {
    const playBtn = this.modal.querySelector('[data-action="toggle-play"]');
    const iconPause = playBtn.querySelector('.icon-pause');
    const iconPlay = playBtn.querySelector('.icon-play');

    if (this.modalVideo.paused) {
      this.modalVideo.play();
      iconPause.style.display = '';
      iconPlay.style.display = 'none';
    } else {
      this.modalVideo.pause();
      iconPause.style.display = 'none';
      iconPlay.style.display = '';
    }
  }

  toggleMute() {
    const volumeBtn = this.modal.querySelector('[data-action="toggle-mute"]');
    const iconOn = volumeBtn.querySelector('.icon-volume-on');
    const iconOff = volumeBtn.querySelector('.icon-volume-off');

    this.modalVideo.muted = !this.modalVideo.muted;

    if (this.modalVideo.muted) {
      iconOn.style.display = 'none';
      iconOff.style.display = '';
    } else {
      iconOn.style.display = '';
      iconOff.style.display = 'none';
    }
  }
}

customElements.define('video-popup', VideoPopup);
